import { Copy, Pencil, Power, RotateCcw, Star } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { AgentDetail } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AgentStatusBadge } from "@/features/agents/components/AgentStatusBadge";
import { AgentVisibilityBadge } from "@/features/agents/components/AgentVisibilityBadge";
import {
  useDuplicateAgent,
  useUpdateAgent,
} from "@/features/agents/hooks/useAgents";
import { getAgentIcon } from "@/lib/agent-icons";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  agent: AgentDetail;
}

export function AgentCard({ agent }: Props) {
  const { t } = useTranslation("agents");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const update = useUpdateAgent(agent.id);
  const dup = useDuplicateAgent();
  const canEdit = useCan(Action.AGENT_UPDATED, {
    owner_team_id: agent.owner_team_id,
    created_by_user_id: agent.created_by_user_id,
  });
  const [confirmInactivate, setConfirmInactivate] = useState(false);

  const Icon = getAgentIcon(agent.icon);
  const isArchived = agent.status === "archived";

  async function handleInactivate() {
    try {
      await update.mutateAsync({ status: "archived" });
      toast.success(t("toasts.inactivated", { name: agent.name }));
      setConfirmInactivate(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleReactivate(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await update.mutateAsync({ status: "draft" });
      toast.success(t("toasts.reactivated", { name: agent.name }));
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDuplicate(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const newAgent = await dup.mutateAsync(agent.id);
      toast.success(`${newAgent.name} criado`);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li
      className="flex flex-col rounded-md border bg-card p-4 shadow-sm transition hover:bg-accent/30 cursor-pointer"
      onClick={() => navigate(`/me/agents/${agent.id}`)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(`/me/agents/${agent.id}`);
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-base font-semibold">{agent.name}</h2>
            <p className="text-xs font-mono text-muted-foreground">
              {agent.slug}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <AgentStatusBadge status={agent.status} />
          <AgentVisibilityBadge visibility={agent.visibility} />
          {agent.agent_type === "external" ? (
            <Badge variant="outline" className="font-normal">
              {t("external.badge")}
            </Badge>
          ) : null}
        </div>
      </div>
      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
        {agent.description || "Sem descrição"}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="secondary" className="font-normal">
          {t("metrics.kbs", { count: agent.kb_count })}
        </Badge>
        <Badge variant="secondary" className="font-normal">
          {t("metrics.mcps", { count: agent.mcp_count })}
        </Badge>
        {agent.rating_count > 0 ? (
          <span className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-current text-yellow-500" />
            {agent.rating_avg?.toFixed(1)} ({agent.rating_count})
          </span>
        ) : null}
      </div>
      <div
        className="mt-3 flex flex-wrap gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {canEdit ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/me/agents/${agent.id}`)}
          >
            <Pencil className="h-3 w-3" />
            {t("actions.edit")}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDuplicate}
          disabled={dup.isPending}
        >
          <Copy className="h-3 w-3" />
          {t("actions.duplicate")}
        </Button>
        {canEdit && isArchived ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleReactivate}
            disabled={update.isPending}
          >
            <RotateCcw className="h-3 w-3" />
            {t("actions.reactivate")}
          </Button>
        ) : null}
        {canEdit && !isArchived ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmInactivate(true);
            }}
            disabled={update.isPending}
          >
            <Power className="h-3 w-3" />
            {t("actions.inactivate")}
          </Button>
        ) : null}
      </div>

      <AlertDialog
        open={confirmInactivate}
        onOpenChange={(v) => !v && setConfirmInactivate(false)}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmInactivate.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmInactivate.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleInactivate}>
              {t("confirmInactivate.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
